/**
 * =============================================================================
 *  DANH BA HO FONT DONG GOI - MOT NGUON SU THAT DUY NHAT
 * =============================================================================
 *
 *  Truoc tep nay chi co MOT ho font (Noto Sans) va o giao dien font bi khoa
 *  cung. Nguoi dung bao "muon doi phong chu"; de doi duoc phong ma khong vi pham
 *  giay phep font, ta dong goi san BA ho font OFL-1.1 - duoc phep ca nhung ca
 *  phan phoi lai:
 *
 *      noto-sans        Noto Sans        (chu khong chan)
 *      noto-serif       Noto Serif       (chu co chan)
 *      be-vietnam-pro   Be Vietnam Pro   (chu khong chan, thiet ke cho tieng Viet)
 *
 *  Ca 12 mat chu (3 ho x 4 mat) deu da duoc kiem glyph: khong thieu mot ky tu
 *  nao tren toan bo bang chu cai tieng Viet cong ASCII in duoc, va OS/2 fsType
 *  khong cam nhung.
 *
 * =============================================================================
 *  VI SAO DANH SACH NAY PHAI NAM O MOT CHO, VA CHI MOT CHO
 * =============================================================================
 *  Ba ben doc danh sach nay:
 *    - tang giao dien (bang thuoc tinh) do ra menu chon phong,
 *    - tang nap font (bundled.ts) tra tung tep .ttf ung voi (ho, mat chu),
 *    - lop hien thi tren man hinh dat `font-family` CSS bang `cssStack`.
 *  Neu moi ben giu mot ban sao danh sach thi ba ban sao se troi khoi nhau - do
 *  chinh la bai hoc so 3 o tang ho font: ban xem truoc ve bang mot phong, tep
 *  xuat ra nhung mot phong khac. Nen danh sach o day la DUY NHAT: menu giao dien
 *  IMPORT tu day, khong duoc go cung mot ban thu hai.
 *
 * =============================================================================
 *  `cssFamily` PHAI KHOP TEN @font-face TRONG styles.css
 * =============================================================================
 *  `cssFamily` la ten ho font ma khoi @font-face trong styles.css khai bao, va
 *  `cssStack` la chuoi `font-family` day du (co ca phong du phong cua he thong).
 *  Lop hien thi ap `cssStack`; neu ten o day lech voi ten @font-face thi trinh
 *  duyet se ve bang mot phong he thong bat ky va "lam dam gia" chu - dung cai
 *  bay ma mat chu that (bundled.ts) duoc dung ra de tranh. Doi ten o day thi
 *  PHAI doi ca styles.css.
 */

/** Ma ho font, on dinh va kebab-case. Di xuyen ca hop dong lenh lan trang thai. */
export type FontFamilyId =
  | 'noto-sans'
  | 'noto-serif'
  | 'be-vietnam-pro'
  | 'tinos'
  | 'cousine';

/** Bon tep .ttf cua mot ho font, theo mat chu. Chi la TEN TEP, khong phai duong dan. */
export interface FontFamilyFiles {
  regular: string;
  bold: string;
  italic: string;
  boldItalic: string;
}

export interface FontFamily {
  /** Ma on dinh dung o hop dong lenh va o trang thai giao dien. */
  id: FontFamilyId;
  /** Nhan hien cho nguoi dung, tieng Viet-than-thien. */
  label: string;
  /** Ten ho font @font-face trong styles.css. PHAI khop - xem ghi chu dau tep. */
  cssFamily: string;
  /** Chuoi `font-family` CSS day du cho lop hien thi tren man hinh. */
  cssStack: string;
  /** Ten tep bon mat chu duoi public/fonts. */
  files: FontFamilyFiles;
}

/**
 * Ho font MAC DINH. Moi duong goi cu khong chi dinh ho font deu roi vao day, nen
 * hanh vi truoc khi co danh ba nay duoc giu nguyen: Noto Sans, bon mat chu.
 */
export const DEFAULT_FONT_FAMILY_ID: FontFamilyId = 'noto-sans';

/**
 * Danh sach CO THU TU. Thu tu nay la thu tu menu giao dien se hien: Noto Sans
 * (mac dinh) dau tien, roi Serif, roi Be Vietnam Pro.
 */
export const FONT_FAMILIES: readonly FontFamily[] = [
  {
    id: 'noto-sans',
    label: 'Noto Sans (chữ không chân)',
    cssFamily: 'PdfEditorNoto',
    cssStack: '"PdfEditorNoto", "Noto Sans", "Segoe UI", sans-serif',
    files: {
      regular: 'NotoSans-Regular.ttf',
      bold: 'NotoSans-Bold.ttf',
      italic: 'NotoSans-Italic.ttf',
      boldItalic: 'NotoSans-BoldItalic.ttf',
    },
  },
  {
    id: 'noto-serif',
    label: 'Noto Serif (chữ có chân)',
    cssFamily: 'PdfEditorNotoSerif',
    cssStack: '"PdfEditorNotoSerif", "Noto Serif", Georgia, serif',
    files: {
      regular: 'NotoSerif-Regular.ttf',
      bold: 'NotoSerif-Bold.ttf',
      italic: 'NotoSerif-Italic.ttf',
      boldItalic: 'NotoSerif-BoldItalic.ttf',
    },
  },
  {
    id: 'be-vietnam-pro',
    label: 'Be Vietnam Pro',
    cssFamily: 'PdfEditorBeVietnam',
    cssStack: '"PdfEditorBeVietnam", "Be Vietnam Pro", "Segoe UI", sans-serif',
    files: {
      regular: 'BeVietnamPro-Regular.ttf',
      bold: 'BeVietnamPro-Bold.ttf',
      italic: 'BeVietnamPro-Italic.ttf',
      boldItalic: 'BeVietnamPro-BoldItalic.ttf',
    },
  },
  {
    // Tinos: tuong thich SO DO voi Times New Roman (cung chieu rong ky tu), nhung
    // la font OFL nhung duoc HOP PHAP. Font Times New Roman that cua Windows KHONG
    // duoc nhung - vi pham giay phep font. Day la cach dung de co "cam giac Times".
    id: 'tinos',
    label: 'Tinos (kiểu Times New Roman)',
    cssFamily: 'PdfEditorTinos',
    cssStack: '"PdfEditorTinos", "Times New Roman", Tinos, serif',
    files: {
      regular: 'Tinos-Regular.ttf',
      bold: 'Tinos-Bold.ttf',
      italic: 'Tinos-Italic.ttf',
      boldItalic: 'Tinos-BoldItalic.ttf',
    },
  },
  {
    // Cousine: tuong thich SO DO voi Courier New (font don khong chan - moi ky tu
    // rong bang nhau), OFL, nhung hop phap.
    id: 'cousine',
    label: 'Cousine (đơn cách, kiểu Courier)',
    cssFamily: 'PdfEditorCousine',
    cssStack: '"PdfEditorCousine", "Courier New", Cousine, monospace',
    files: {
      regular: 'Cousine-Regular.ttf',
      bold: 'Cousine-Bold.ttf',
      italic: 'Cousine-Italic.ttf',
      boldItalic: 'Cousine-BoldItalic.ttf',
    },
  },
];

const BY_ID = new Map<string, FontFamily>(FONT_FAMILIES.map((f) => [f.id, f]));

/** Chuoi co phai ma ho font hop le hay khong. Dau vao tu hop dong la string tho. */
export function isFontFamilyId(id: string | null | undefined): id is FontFamilyId {
  return typeof id === 'string' && BY_ID.has(id);
}

/**
 * Lay ban ghi ho font. Ma khong hop le (hoac bo trong) LUI VE ho mac dinh chu
 * khong nem loi: mot lenh cu khong mang `fontFamily`, hoac mang mot ma tu phien
 * ban tuong lai, van phai ghi ra duoc - bang phong mac dinh, khong phai bang mot
 * o trong.
 */
export function getFontFamily(id: string | null | undefined): FontFamily {
  return (id && BY_ID.get(id)) || (BY_ID.get(DEFAULT_FONT_FAMILY_ID) as FontFamily);
}

/** Chuan hoa mot ma tho ve mot ma hop le (hoac mac dinh). */
export function normalizeFontFamilyId(id: string | null | undefined): FontFamilyId {
  return isFontFamilyId(id) ? id : DEFAULT_FONT_FAMILY_ID;
}

/** Duong dan cong khai cua mot tep font. Vite phuc vu public/ tai goc site. */
export function fontFileUrl(fileName: string): string {
  return `/fonts/${fileName}`;
}
